import { useEffect, useState } from 'react'
import { RowEvent } from '@amzn/ag-bird/src/ag-grid-enterprise'
import { CustomCellRendererProps } from 'ag-grid-react'
import { ColDef, ColGroupDef, IAggFuncParams, IRowNode } from 'ag-grid-community'
import { SpaceBetween } from '@amzn/awsui-components-react'
import { EMPLOYEE_TYPE, NOT_APPLICABLE } from '../../../Constant'
import { getTwoDecimal, isNumber, isZero } from '../../../common/Util'
import { orderBy } from 'lodash'

interface extendedColGroupDef extends ColGroupDef {
    hr_headcount?: any
}

const GRAND_TOTAL_ROW_HEIGHT = 32
const NORMAL_ROW_HEIGHT = 50
const GROUP_HEADER_ROW_HEIGHT = 75
const SUB_HEADER_ROW_HEIGHT = 30
const TOTAL_COLUMN_WIDTH = 100
const NORMAL_COLUMN_WIDTH = 250

const TOTAL_COLOR_CODE = '#F8F6F5'
const NORMAL_CELL_COLOR_CODE = '#FFFFFF'
const DELTA_GREEN_STYLEID = 'manager-dashboard-text-style-green'
const DELTA_RED_STYLEID = 'manager-dashboard-text-style-orange'

const EMPLOYEE_HEADERS = { employee: 'Employee', ct: 'C&T', ff: 'FF', other: 'Other' }
const NULL_COLUMN_NAMES = [
    ...Object.keys(EMPLOYEE_HEADERS).map((k) => `_${k}`),
    'headcount',
    'total',
]

const isInParentRow = (node: IRowNode) =>
    node.group && (node.allChildrenCount ?? 0) > 0 && !node.footer
const isInGrandTotalRow = (node: IRowNode) => node.level === -1

const customCellComponent = (props: CustomCellRendererProps) => {
    const { node, value } = props
    const [expanded, setExpanded] = useState<boolean>(node.expanded)

    useEffect(() => {
        const expandListener = (event: RowEvent) => setExpanded(event.node.expanded)
        node.addEventListener('expandedChanged', expandListener)

        return () => {
            node.removeEventListener('expandedChanged', expandListener)
        }
    }, [])

    if (expanded && isInParentRow(node)) {
        return ''
    }
    if (isNumber(value)) {
        return getTwoDecimal(value)
    }
    return value || ''
}

export const generateTeamAllocationSummaryGridOptions = () => {
    return {
        defaultColDef: {
            cellRendererParams: {
                innerRenderer: (props: CustomCellRendererProps) => {
                    const { node, value } = props
                    if (!node.footer) {
                        return <span>{value}</span>
                    }

                    // footer: sub/grand total row
                    return isInGrandTotalRow(node) ? (
                        <SpaceBetween size='xxxs'>
                            <span>Grand Total</span>
                            <span>Expected HC</span>
                            <span>Delta HC</span>
                        </SpaceBetween>
                    ) : (
                        <span>Sub Total</span>
                    )
                },
            },
        },
        getRowStyle: ({ node }) => {
            return {
                fontWeight: node.footer ? 'bold' : 'normal',
                background: node.footer ? TOTAL_COLOR_CODE : NORMAL_CELL_COLOR_CODE,
            }
        },
        getRowHeight: ({ node }) =>
            isInGrandTotalRow(node) ? GRAND_TOTAL_ROW_HEIGHT : NORMAL_ROW_HEIGHT,
        alwaysShowHorizontalScroll: true,
        enableBrowserTooltips: true,
        groupSuppressBlankHeader: true,
        groupHeaderHeight: GROUP_HEADER_ROW_HEIGHT,
        headerHeight: SUB_HEADER_ROW_HEIGHT,
        loadingCellRenderer: 'LoadingCellRenderer',
        pagination: true,
        suppressAggFuncInHeader: true,
        suppressColumnVirtualisation: true,
        suppressRowVirtualisation: true,
    }
}

export const nullOutZeroAndDivideValuesByNumberOfWeek = (rows: any[], numberOfWeek: number) => {
    // null out zero number from the table and if the view is monthly, divide allocation by number of weeks in the month to get average monthly allocation
    return orderBy(rows, ['program_name', 'project_name'], ['asc', 'asc']).map((row) => {
        const updated_row = {}
        Object.keys(row).forEach((element) => {
            if (isZero(row[element])) {
                updated_row[element] = ''
                return
            }
            if (numberOfWeek && NULL_COLUMN_NAMES.some((sub) => element.includes(sub))) {
                updated_row[element] = String(Number(row[element]) / numberOfWeek)
                return
            }
            updated_row[element] = row[element]
        })
        return updated_row
    })
}

export const customAggSum = (params: IAggFuncParams) => {
    let sum = 0
    params.values.forEach((value) => {
        if (!value) {
            return
        }

        if (Array.isArray(value)) {
            if (!value.length) {
                return
            }
            value = value[0]
        }
        if (typeof value === 'string') {
            value = parseFloat(value)
        }
        sum += value
    })

    if (!isInGrandTotalRow(params.rowNode) || params.colDef.headerName === 'Total') {
        return sum ? getTwoDecimal(sum) : ''
    }

    // Last row -> Grand Total/Expected HC/Delta HC
    const colGroupDef = params.column.getOriginalParent()?.getColGroupDef() as extendedColGroupDef
    const hrHeadcount = colGroupDef?.hr_headcount

    let expectedHC = NOT_APPLICABLE
    let delta = '0.00'
    let styleId = ''

    if (hrHeadcount) {
        // for team headcount columns, params.colDef.field = [teamID]_[empType]
        const employeeTypes = params.colDef.field?.split('_')?.[1] ?? ''
        expectedHC = getTwoDecimal(hrHeadcount[employeeTypes])
        delta = getTwoDecimal(sum - +expectedHC)
    }

    if (expectedHC === NOT_APPLICABLE) {
        delta = NOT_APPLICABLE
    } else {
        styleId = isZero(delta) ? DELTA_GREEN_STYLEID : DELTA_RED_STYLEID
    }

    return (
        <SpaceBetween size='xxxs'>
            <span>{getTwoDecimal(sum)}</span>
            <span>{expectedHC}</span>
            <span id={styleId}>{delta}</span>
        </SpaceBetween>
    )
}

export const getFormatFixedColumns = (
    teams: any[],
    customHeaderGroup: any,
    customHeaderGroupParams: any,
) => {
    let columnDefs: (ColDef | ColGroupDef | extendedColGroupDef)[] = [
        {
            field: 'program_name',
            tooltipField: 'program_name',
            rowGroup: true,
            showRowGroup: true,
            headerName: 'Program',
            width: NORMAL_COLUMN_WIDTH,
            hide: true,
        },
        {
            field: 'project_name',
            tooltipField: 'project_name',
            headerName: 'Project',
            width: NORMAL_COLUMN_WIDTH,
            pinned: 'left',
        },
        {
            field: 'total_allocation',
            tooltipField: 'total_allocation',
            headerName: 'Total',
            aggFunc: customAggSum,
            cellRenderer: customCellComponent,
            width: TOTAL_COLUMN_WIDTH,
        },
    ]

    orderBy(teams, ['team_name'], ['asc']).forEach((team) => {
        const hasCT =
            team.employee_types?.includes(EMPLOYEE_TYPE.CT) &&
            Object.values(team.scoped_weeks).includes(true)
        let employeeTypes = [EMPLOYEE_TYPE.CT, EMPLOYEE_TYPE.FF, EMPLOYEE_TYPE.OTHER]
        if (!hasCT) {
            employeeTypes = [EMPLOYEE_TYPE.EMPLOYEE, EMPLOYEE_TYPE.OTHER]
        }

        columnDefs.push({
            headerName: team.team_name,
            headerGroupComponent: customHeaderGroup,
            headerGroupComponentParams: { ...customHeaderGroupParams, team },
            hr_headcount: team.hr_headcount,
            children: employeeTypes.map((emp) => ({
                field: `${team.team_id}_${emp}`,
                headerName: EMPLOYEE_HEADERS?.[emp] || emp,
                aggFunc: customAggSum,
                cellRenderer: customCellComponent,
                autoHeight: true,
                width: NORMAL_COLUMN_WIDTH / employeeTypes.length,
            })),
        })
    })
    return columnDefs
}
